import { createContext, useContext, useState, Dispatch, SetStateAction } from "react";

interface FileContextType {
  fileIds: string[];
  setFileIds: Dispatch<SetStateAction<string[]>>;
  pendingImages: string[];
  setPendingImages: Dispatch<SetStateAction<string[]>>;
  addPendingImage: (imageUri: string) => void;
  removePendingImage: (index: number) => void;
  clearPendingImages: () => void;
  isImageUploading: boolean;
  setIsImageUploading: (isUploading: boolean) => void;
  imageUploadError: string | null;
  setImageUploadError: (error: string | null) => void;
}

const FileContext = createContext<FileContextType>({
  fileIds: [],
  setFileIds: () => [],
  pendingImages: [],
  setPendingImages: () => {},
  addPendingImage: () => {},
  removePendingImage: () => {},
  clearPendingImages: () => {},
  isImageUploading: false,
  setIsImageUploading: () => {},
  imageUploadError: null,
  setImageUploadError: () => {},
});

export const FileContextWrapper = ({
  children,
}: {
  children: React.ReactNode;
}) => {
  const [fileIds, setFileIds] = useState<string[]>([]);
  const [pendingImages, setPendingImages] = useState<string[]>([]);
  const [isImageUploading, setIsImageUploading] = useState(false);
  const [imageUploadError, setImageUploadError] = useState<string | null>(null);

  const addPendingImage = (imageUri: string) => {
    setPendingImages((prev) => [...prev, imageUri]);
  };

  const removePendingImage = (index: number) => {
    setPendingImages((prev) => prev.filter((_, i) => i !== index));
  };

  const clearPendingImages = () => {
    setPendingImages([]);
  };

  const value = {
    fileIds,
    setFileIds,
    pendingImages,
    setPendingImages,
    addPendingImage,
    removePendingImage,
    clearPendingImages,
    isImageUploading,
    setIsImageUploading,
    imageUploadError,
    setImageUploadError,
  };

  return <FileContext.Provider value={value}>{children}</FileContext.Provider>;
};

export const useFileContext = () => useContext(FileContext);
